import { addDays, format, isWeekend, parseISO } from 'date-fns';
import type { OfficeLocation } from '../types/hr';
import type { Holiday } from '../types/selfService';

export function holidayApplies(h: Holiday, policyId?: string, location?: OfficeLocation): boolean {
  const policyOk = h.policies.length === 0 || (policyId ? h.policies.includes(policyId) : false);
  const officeOk = h.offices.length === 0 || (location ? h.offices.includes(location) : false);
  return policyOk && officeOk;
}

export function holidayMap(holidays: Holiday[]): Map<string, Holiday> {
  return new Map(holidays.map((h) => [h.date, h]));
}

export function sortHolidays(holidays: Holiday[]): Holiday[] {
  return [...holidays].sort((a, b) => a.date.localeCompare(b.date));
}

export function upcomingHolidays(holidays: Holiday[], fromIso: string, count: number): Holiday[] {
  return sortHolidays(holidays)
    .filter((h) => h.date >= fromIso)
    .slice(0, count);
}

export interface DayBreakdown {
  /** Days charged against the balance (weekends and full holidays excluded, half days = 0.5). */
  days: number;
  weekendDays: number;
  holidays: Holiday[];
}

/** Business-day math for a time-off request. */
export function countRequestDays(startIso: string, endIso: string, holidays: Holiday[], halfFirst: boolean, halfLast: boolean): DayBreakdown {
  const map = holidayMap(holidays);
  const end = parseISO(endIso);
  let cursor = parseISO(startIso);
  let days = 0;
  let weekendDays = 0;
  const hit: Holiday[] = [];

  while (cursor <= end) {
    const iso = format(cursor, 'yyyy-MM-dd');
    if (isWeekend(cursor)) {
      weekendDays += 1;
    } else {
      const h = map.get(iso);
      if (h) hit.push(h);
      if (!h || h.halfDay) {
        let value = h?.halfDay ? 0.5 : 1;
        const isFirst = iso === startIso;
        const isLast = iso === endIso;
        if ((isFirst && halfFirst) || (isLast && halfLast && !isFirst)) value = Math.min(value, 0.5);
        days += value;
      }
    }
    cursor = addDays(cursor, 1);
  }

  return { days, weekendDays, holidays: hit };
}

export function appliesToLabel(h: Holiday, policyName: (id: string) => string): string {
  const policies = h.policies.length ? h.policies.map(policyName).join(', ') : 'All policies';
  const offices = h.offices.length ? h.offices.join(', ') : 'All offices';
  return `${policies} · ${offices}`;
}
